import { T } from "./Translate";
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  Box,
  CardMedia,
  Skeleton,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MediaQuery from "react-responsive";
import { ArrowBackIos as ArrowBackIosIcon } from "../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { Edit as EditIcon } from "../icons";
import { DeleteOutline as DeleteIcon } from "../icons";
import BottomToolbar from "./BottomToolbar";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLoggedIn } from "../session";
import { auth, db, getToken } from "../firebase";
import Atras from "./BackButton";
import EditSectionTabs from "./EditSectionTabs";
import LocationAutocompleteField from "../components/LocationAutocompleteField";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { brandColors, barColors } from "../theme";
import { pageContentSx } from "../pageLayout";
import { uploadButtonSx, uploadProgressSx } from "./mediaFormStyles";
import SortableImageGrid from "../components/SortableImageGrid";

import SearchClearAdornment from "../components/SearchClearAdornment";
const pageHeaderInnerSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  maxWidth: { xs: "100%", md: "980px" },
  mx: "auto",
  px: 2,
  boxSizing: "border-box",
};

// Mismos ids que PROPERTY_TYPES en trips/trips.js (no se importa desde ahí
// para no depender de un componente de página; son solo los valores del
// campo "category" en Firestore).
// Continente de la propiedad (`place`), mismas opciones que publishProperty.js
// y el filtro por región de trips.js.
const PROPERTY_REGIONS = [
  "África",
  "Asia",
  "América del Norte",
  "América del Sur",
  "Europa",
  "Medio Oriente",
  "Oceanía",
  "Antártida",
];

const PROPERTY_CATEGORIES = [
  { id: "alojamiento", label: "Alojamiento" },
  { id: "compra", label: "Compra" },
  { id: "alquiler", label: "Alquiler" },
  { id: "combo", label: "Combo Vuelo + Alojamiento" },
];

const normalizeMediaArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

function EditTrips() {
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(160);

  // Mide el alto real del AppBar fijo en vez de adivinar un valor en px (ver
  // mismo criterio en editOffers.js/home.js/events.js).
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) return;
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const [open, setOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  const [loading, setLoading] = useState(true);

  const [images, setImages] = useState([]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  // "Dirección" con sugerencias: se guarda en `address`, y la ciudad de la
  // sugerencia elegida en `city` (la usan los filtros y el orden por ciudad
  // de trips.js). Si se tipea sin elegir sugerencia, se conserva la ciudad.
  // `place` es el continente (filtro por región).
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [place, setPlace] = useState("");
  const [coords, setCoords] = useState(null);
  const [category, setCategory] = useState("");
  const [video, setVideo] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");

  const handleEditClick = (propertyId) => {
    const selected = properties.find((p) => p.id === propertyId);
    if (!selected) return;
    setSelectedPropertyId(propertyId);
    setTitle(selected.title || "");
    setDescription(selected.description || "");
    setPrice(selected.price != null ? String(selected.price) : "");
    setCity(selected.city || "");
    setAddress(selected.address || selected.city || "");
    setPlace(selected.place || "");
    setCoords(null);
    setCategory(selected.category || "");
    setVideo(selected.video || "");
    setYoutubeUrl(selected.youtubeUrl || "");
    setImages(normalizeMediaArray(selected.images));
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleSubmit = async () => {
    if (!title || !description || !price || !address.trim() || !place || !category) {
      alert("Por favor, completa todos los campos obligatorios.");
      return;
    }

    try {
      const propertyRef = doc(db, "places", selectedPropertyId);
      await updateDoc(propertyRef, {
        title,
        description,
        price: Number(price),
        address: address.trim(),
        city: city || address.trim(),
        ...(coords || {}),
        place,
        category,
        images,
        video: video || null,
        youtubeUrl: youtubeUrl || "",
      });
      handleClose();
    } catch (error) {
      console.error("Error al actualizar la propiedad:", error);
    }
  };

  const handleImageUpload = () => {};
  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };
  const handlePriceChange = (e) => setPrice(e.target.value);
  const handleDelete = async (propertyId) => {
    try {
      const propertyRef = doc(db, "places", propertyId);
      await deleteDoc(propertyRef);
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
    }
  };

  const [properties, setProperties] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [resultado, setResultados] = useState([]);
  // Muestra de a 10 publicaciones y suma otras 10 al hacer scroll; vuelve a
  // la primera tanda cuando cambia la búsqueda.
  const { limit: listLimit, sentinelRef: listSentinelRef } = useInfiniteScroll(busqueda, 10);

  const buscar = useCallback(() => {
    const valorBusqueda = busqueda.toLowerCase();
    const resultadosFiltrados = properties.filter((property) =>
      (property.title || "").toLowerCase().includes(valorBusqueda)
    );
    setResultados(resultadosFiltrados);
  }, [busqueda, properties]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const formatDate = (date) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    } else if (date && date.toDate instanceof Function) {
      return date.toDate().toLocaleDateString();
    }
    return "";
  };

  const navigate = useNavigate();

  // ?edit=<id> abre directo el editor de esa publicación (ej. desde el menú ⋮
  // del perfil). Se saca el parámetro para que no se vuelva a abrir.
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam || !properties.some((item) => item.id === editParam)) return;
    handleEditClick(editParam);
    setSearchParams({}, { replace: true });
  }, [editParam, properties]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkAuthAndFetchProperties = async () => {
      if (!isLoggedIn()) {
        navigate("/login");
        return;
      }

      try {
        const token = await getToken();

        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }
        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "places"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const propertiesData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            createdAt: doc.data().createdAt ? doc.data().createdAt.toDate() : null,
          }));
          setProperties(propertiesData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener propiedades:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchProperties();
  }, [navigate]);

  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  const handleOpenImageModal = (images) => {
    setModalImages(images);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === modalImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? modalImages.length - 1 : prevIndex - 1
    );
  };

  const styles = {
    listItem: {
      display: "block",
      width: "100%",
      border: "none",
      margin: "16px auto",
      padding: "clamp(14px, 4vw, 20px)",
      boxSizing: "border-box",
      // Links largos sin espacios en la descripción ensanchaban la tarjeta
      // más allá de la pantalla en celular.
      overflowWrap: "anywhere",
      backgroundColor: brandColors.backgroundPaper,
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      },
    },
    title: {
      fontSize: "20px",
      marginBottom: "12px",
      color: brandColors.textPrimary,
      fontWeight: "500",
      lineHeight: "1.4",
    },
    description: {
      marginBottom: "16px",
      fontWeight: "400",
      color: brandColors.textSecondary,
      lineHeight: "1.6",
      fontSize: "15px",
    },
    price: {
      fontSize: "17px",
      marginBottom: "16px",
      backgroundColor: "#1f2a63",
      color: "#c7d2ff",
      padding: "12px 16px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: "500",
    },
    specs: {
      marginBottom: "16px",
      color: brandColors.textSecondary,
      fontSize: "14px",
    },
    created: {
      fontSize: "10px",
      marginBottom: "15px",
      marginTop: "15px",
    },
    noImage: {
      width: "100%",
      height: "100px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: brandColors.backgroundDefault,
      color: brandColors.textSecondary,
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "200px",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    additionalImagesOverlay: {
      position: "absolute",
      bottom: "10px",
      right: "10px",
      width: "60px",
      height: "60px",
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      color: "white",
      cursor: "pointer",
    },
    additionalImagesCount: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#ffffff",
    },
    imageModalContent: {
      maxWidth: "90%",
      maxHeight: "90%",
      position: "relative",
      backgroundColor: "#1e1e1e",
      padding: "20px",
      borderRadius: "10px",
      boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.5)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    imageModalImage: {
      width: "100%",
      height: "auto",
      borderRadius: "10px",
      marginBottom: "20px",
    },
    imageModalNavigation: {
      position: "absolute",
      bottom: "10px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      justifyContent: "space-between",
      width: "auto",
      padding: "10px",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: "5px",
    },
  };

  const renderPropertyCard = (property) => (
    <ListItem key={property.id} style={styles.listItem}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "30%" },
            mr: { xs: 0, md: 2 },
            mb: { xs: 2, md: 0 },
          }}
        >
          {property.images && property.images.length > 0 ? (
            <Box style={styles.imageContainer}>
              <CardMedia
                component="img"
                image={property.images[0]}
                alt={`Imagen principal de ${property.title}`}
                style={styles.mainImage}
              />
              {property.images.length > 1 && (
                <Box
                  style={styles.additionalImagesOverlay}
                  onClick={() => handleOpenImageModal(property.images)}
                >
                  <Typography style={styles.additionalImagesCount}>
                    +{property.images.length - 1}
                  </Typography>
                  <Typography sx={{ color: "#ffffff" }}><T>Ver más</T></Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box style={styles.noImage}>
              <Typography><T>No hay imágenes disponibles</T></Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          <Typography style={styles.title}>{property.title}</Typography>
          <Typography style={styles.description}>{property.description}</Typography>
          <Typography style={styles.price}>
            <T>Precio:</T> ${property.price}
          </Typography>
          <Typography style={styles.specs}>
            {property.place}
            {property.city ? ` — ${property.city}` : ""}
            {property.category ? ` — ${property.category}` : ""}
          </Typography>
          <Typography style={styles.created}>
            <T>Fecha de publicación:</T> {formatDate(property.createdAt)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon />}
              onClick={() => handleEditClick(property.id)}
              sx={{ textTransform: "none" }}
            >
              <T>Editar</T>
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(property.id)}
              sx={{ textTransform: "none" }}
            >
              <T>Eliminar</T>
            </Button>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );

  const renderEditFields = () => (
    <>
      <TextField
        required
        autoFocus
        margin="dense"
        label={<T>Título</T>}
        type="text"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <TextField
        required
        margin="dense"
        label={<T>Descripción</T>}
        type="text"
        multiline
        rows={2}
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <TextField
        required
        margin="dense"
        label={<T>Precio en USD</T>}
        type="number"
        fullWidth
        value={price}
        onChange={handlePriceChange}
      />
      <LocationAutocompleteField
        mode="address"
        required
        margin="dense"
        label={<T>Dirección</T>}
        value={address}
        onChange={setAddress}
        onSelect={(opt) => {
          if (opt.city) setCity(opt.city);
          setCoords({ latitude: opt.latitude, longitude: opt.longitude });
        }}
      />
      <TextField
        select
        required
        margin="dense"
        label={<T>Continente</T>}
        fullWidth
        value={PROPERTY_REGIONS.includes(place) ? place : ""}
        onChange={(e) => setPlace(e.target.value)}
      >
        {PROPERTY_REGIONS.map((region) => (
          <MenuItem key={region} value={region}>
            <T>{region}</T>
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        required
        margin="dense"
        label={<T>Categoría</T>}
        fullWidth
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        {PROPERTY_CATEGORIES.map((option) => (
          <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        margin="dense"
        label={<T>URL de video</T>}
        type="text"
        fullWidth
        value={video}
        onChange={(e) => setVideo(e.target.value)}
      />
      <TextField
        margin="dense"
        label={<T>URL de YouTube</T>}
        type="text"
        fullWidth
        value={youtubeUrl}
        onChange={(e) => setYoutubeUrl(e.target.value)}
      />

      <label
        htmlFor="upload-property-image-button"
        style={{ width: "100%", marginTop: "15px" }}
      >
        <Button
          variant="contained"
          fullWidth
          component="div"
          sx={uploadButtonSx(false)}
          disabled={loading}
        >
          <AddPhotoAlternateIcon sx={{ color: "white" }} />
          <T>Subir Imágenes</T>
          {loading && (
            <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
          )}
        </Button>
      </label>
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        style={{ display: "none" }}
        id="upload-property-image-button"
        multiple
      />

      <SortableImageGrid
        images={images}
        onChange={setImages}
        onRemove={handleRemoveImage}
      />
    </>
  );

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar ref={appBarRef} position="fixed" color="primary">
        <Toolbar disableGutters sx={{ padding: "10px 0" }}>
          <Box sx={pageHeaderInnerSx}>
            <Atras color="inherit" />
            <Typography variant="h5">
              <T>Publicaciones</T>
            </Typography>
          </Box>
        </Toolbar>
        <EditSectionTabs />
        <Box sx={{ borderTop: `1px solid ${barColors.border}`, py: 1 }}>
          <Box sx={pageHeaderInnerSx}>
            <TextField
              id="search-trips-input"
              fullWidth
              size="small"
              placeholder="Buscar por título de la propiedad..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoComplete="off"
              InputProps={{
                endAdornment: <SearchClearAdornment value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />,
                startAdornment: <SearchIcon sx={{ mr: 1, color: barColors.textMuted }} />,
                sx: {
                  color: barColors.text,
                  backgroundColor: barColors.activeBg,
                  borderRadius: "999px",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Box>
      </AppBar>
      <Container disableGutters sx={{ mt: `${appBarHeight}px`, mb: 10 }}>
        <div>
          {loading ? (
            <List>
              {[1, 2, 3].map((i) => (
                <ListItem key={i} style={{ display: "block", padding: "20px", marginBottom: "16px", backgroundColor: brandColors.backgroundPaper, borderRadius: "8px" }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Skeleton variant="rectangular" width="30%" height={150} sx={{ borderRadius: "8px" }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: "6px", mb: 1 }} />
                      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: "6px" }} />
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <>
              {busqueda.trim() !== "" ? (
                resultado.length > 0 ? (
                  <List sx={{ pb: 4 }}>{resultado.slice(0, listLimit).map(renderPropertyCard)}</List>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Typography variant="h6"><T>No encontramos resultados</T></Typography>
                    <Typography variant="body2"><T>No hay publicaciones que coincidan con tu búsqueda.</T></Typography>
                  </Box>
                )
              ) : (
                <List sx={{ pb: 4 }}>{properties.slice(0, listLimit).map(renderPropertyCard)}</List>
              )}
              {(busqueda.trim() !== "" ? resultado : properties).length > listLimit && (
                <Box ref={listSentinelRef} sx={{ height: 1 }} />
              )}
            </>
          )}
        </div>
      </Container>
      <Dialog open={open} onClose={handleClose} sx={{ zIndex: 20000 }}>
        <DialogTitle><T>Editar propiedad</T></DialogTitle>
        <DialogContent>
          <MediaQuery minWidth={1224}>
            {renderEditFields()}
          </MediaQuery>
          <MediaQuery maxWidth={1223}>
            {renderEditFields()}
          </MediaQuery>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <T>Cancelar</T>
          </Button>
          <Button onClick={handleSubmit} color="primary">
            <T>Guardar</T>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-labelledby="image-modal-title"
        fullScreen
      >
        <DialogTitle id="image-modal-title"><T>Imágenes de la propiedad</T></DialogTitle>
        <DialogContent>
          {openImageModal && (
            <Box style={styles.imageModalContent}>
              {modalImages && modalImages.length > 0 ? (
                <CardMedia
                  component="img"
                  image={modalImages[currentImageIndex]}
                  alt={`Imagen ${currentImageIndex + 1} de la propiedad`}
                  style={styles.imageModalImage}
                />
              ) : (
                <Typography><T>No hay imágenes disponibles</T></Typography>
              )}
              <Box style={styles.imageModalNavigation}>
                <IconButton onClick={handlePreviousImage}>
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton onClick={handleNextImage}>
                  <ArrowForwardIosIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageModal}><T>Cerrar</T></Button>
        </DialogActions>
      </Dialog>
      <BottomToolbar />
    </Container>
  );
}

export default EditTrips;
